import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// New-version detection. Nothing is cached (no fetch handler in the SW), so an
// already-open tab/PWA keeps its old bundles until reloaded. Re-fetch
// index.html un-cached and compare its script URLs (content-hashed by the
// build) with the ones this page loaded; a difference means a new deploy.
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private available = new BehaviorSubject<boolean>(false);
  readonly updateAvailable$ = this.available.asObservable();
  private loadedScripts = this.scriptList(document);

  constructor(private ngZone: NgZone) { }

  get updateAvailable(): boolean { return this.available.value; }

  // Poll every 10 min and whenever the tab becomes visible. Outside the zone so
  // the timer doesn't trigger change detection every tick.
  start(): void {
    this.ngZone.runOutsideAngular(() => {
      setInterval(() => this.check(), 10 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.check();
      });
    });
  }

  // Resolves true when a newer build is deployed. Also used by the manual
  // "Check for updates" menu item.
  async check(): Promise<boolean> {
    if (this.available.value) return true;
    try {
      const res = await fetch(document.baseURI, { cache: 'no-store' });
      const latest = this.scriptList(new DOMParser().parseFromString(await res.text(), 'text/html'));
      // latest === '' => got an error/login page rather than the app shell
      if (latest && latest !== this.loadedScripts) {
        this.ngZone.run(() => this.available.next(true));
      }
    } catch { /* offline or blip - caller sees "no update" */ }
    return this.available.value;
  }

  reload(): void {
    window.location.reload();
  }

  private scriptList(doc: Document): string {
    return Array.from(doc.querySelectorAll('script[src]'))
      .map(s => s.getAttribute('src'))
      .filter(src => !!src && !/^https?:\/\//.test(src as string))
      .sort()
      .join('|');
  }
}
